import React, {useEffect, useState} from "react";
import { Button } from "@progress/kendo-react-buttons";
import {Dialog, DialogActionsBar} from "@progress/kendo-react-dialogs";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { Input } from "@progress/kendo-react-inputs";
import {Label} from "@progress/kendo-react-labels";
import {useFormik} from "formik";
import LoadOverlay from "../SharedComponents/LoadOverlay";
import "./S3Settings.css";

import {
    deleteSettings,
    saveSettings,
    testExistingConnection,
    testNewConnection,
    updateSettings,
} from "../../../actions/OnBoardingActions";
import * as Yup from "yup";
import {S3_COMPATIBLE_STRING} from "../../../api/constants";

const awsS3Regions = [
    "us-east-1",
    "us-east-2",
    "us-west-1",
    "us-west-2",
    "ca-central-1",
    "eu-central-1",
    "eu-west-1",
    "eu-west-2",
    "eu-west-3",
    "ap-south-1",
    "ap-northeast-1",
    "ap-northeast-2",
    "cn-north-1",
    "cn-northwest-1",
    "sa-east-1"
];

const S3CompatibleSettings = (props) => {
    const [pageLoader, setPageLoader] = useState(false);
    const [visible, setVisible] = useState(props.visible);
    const [disableSubmit, setDisableSubmit] = useState(false);
    const [disableTestConnection, setDisableTestConnection] = useState(false);

    const temporarilyDisableButton = (setter, duration = 3000) => {
        setter(true);
        setTimeout(() => {
            setter(false);
        }, duration);
    };

    const isAwsS3Region = (host) => awsS3Regions.includes((host || "").trim().toLowerCase());
    const normalizeEndpointHost = (value) => {
        const raw = (value || "").trim();
        if (!raw) {
            return "";
        }

        try {
            if (/^https?:\/\//i.test(raw)) {
                return new URL(raw).hostname || raw;
            }
        } catch {
            return raw;
        }

        return raw.split("/")[0];
    };
    const splitStartingPath = (value) => {
        const raw = (value || "").trim().replace(/^\/+/, "");
        if (!raw) {
            return { bucket: "", prefix: "" };
        }

        const [bucket, ...rest] = raw.split("/");
        return {
            bucket: bucket || "",
            prefix: rest.join("/")
        };
    };
    const composeStartingPath = (bucket, prefix) => {
        const normalizedBucket = (bucket || "").trim().replace(/^\/+|\/+$/g, "");
        const normalizedPrefix = (prefix || "").trim().replace(/^\/+/, "");

        if (!normalizedBucket) {
            return "";
        }

        return normalizedPrefix ? `${normalizedBucket}/${normalizedPrefix}` : normalizedBucket;
    };

    const s3Formik = useFormik({
        initialValues: {
            connection_name: "",
            host: "",
            bucket: "",
            prefix: "",
            user_name: "",
            password: "",
            transfer_protocol: "s3c"
        },
        validationSchema: Yup.object().shape({
            host: Yup.string()
                .required("Endpoint host is required")
                .test("no-scheme", "Enter a hostname only, without http:// or https://", (value) => {
                    return value ? !/^https?:\/\//i.test(value.trim()) : true;
                })
                .test("no-path", "Enter a hostname only, without any path", (value) => {
                    return value ? !value.trim().includes("/") : true;
                }),
            connection_name: Yup.string().required("Connection name is required")
                .test("no-apostrophes", "The string must not contain apostrophes", (value) => {
                    return value && !value.includes("'");
                }),
            bucket: Yup.string()
                .required("Bucket is required")
                .test("no-slash", "Bucket should not include any path", (value) => {
                    return value ? !value.trim().includes("/") : true;
                }),
            user_name: Yup.string().required("Access key is required"),
            password: Yup.string().required("Secret key is required")
        }),
        onSubmit: () => {
        },
    });

    const createPayload = () => {
        let startingPath = composeStartingPath(s3Formik.values.bucket, s3Formik.values.prefix);
        const delimiter = "/";
        const hasFilePattern = startingPath.includes("*") || startingPath.includes("?");

        if (isAwsS3Region(s3Formik.values.host) && !hasFilePattern && delimiter && startingPath.split(delimiter).length > 1 && !startingPath.endsWith(delimiter)) {
            startingPath += delimiter;
        }

        return {
            connection_name: s3Formik.values.connection_name,
            host: normalizeEndpointHost(s3Formik.values.host),
            delimiter: delimiter,
            password: s3Formik.values.password,
            starting_path: startingPath,
            transfer_protocol: s3Formik.values.transfer_protocol,
            user_name: s3Formik.values.user_name
        };
    };

    const testS3CompatibleSettings = async (editDialogSubmit = false) => {
        const payload = createPayload();
        setPageLoader(true);
        temporarilyDisableButton(setDisableTestConnection);
        if (props.mode === "EDIT") {
            testExistingConnection(payload, null, S3_COMPATIBLE_STRING, editDialogSubmit).then((res) => {
                setPageLoader(false);
                return res;
            });
        } else {
            testNewConnection(payload, S3_COMPATIBLE_STRING, editDialogSubmit).then((res) => {
                setPageLoader(false);
                return res;
            });
        }
    };

    const saveS3CompatibleSettings = async () => {
        const payload = createPayload();
        let response = null;

        if (props.mode === "EDIT") {
            response = await updateSettings(payload, props.s3Settings.id, s3Formik.values.connection_name);
        } else {
            response = await saveSettings(payload, s3Formik.values.connection_name);
        }

        if (!response) {
            setPageLoader(false);
            return false;
        }

        props.getAllSavedSettings("S3");
        setPageLoader(false);
        s3Formik.resetForm();

        await props.refreshTable().then(() => {
            setVisible(!visible);
            props.setS3CompatibleVisible(false);
        });

        return true;
    };

    const removeS3CompatibleSettings = async () => {
        setPageLoader(true);
        await deleteSettings(props.s3Settings.id, s3Formik.values.connection_name).then(async () => {
            setPageLoader(false);
            props.getAllSavedSettings();
            s3Formik.resetForm();

            await props.refreshTable().then(() => {
                setVisible(!visible);
                props.setS3CompatibleVisible(false);
            });
        });
    };

    useEffect(() => {
        if (props.mode === "EDIT") {
            const { bucket, prefix } = splitStartingPath(props.s3Settings.starting_path);
            s3Formik.setValues({
                connection_name: props.s3Settings.connection_name || "",
                host: normalizeEndpointHost(props.s3Settings.host) || "",
                bucket: bucket || "",
                prefix: prefix || "",
                user_name: props.s3Settings.user_name || "",
                password: "",
                transfer_protocol: props.s3Settings.transfer_protocol || "s3c"
            });
        }
    }, [props.mode, props.s3Settings]);

    const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState(false);

    const deleteConfirmed = () => {
        setShowDeleteConfirmationDialog(false);
        removeS3CompatibleSettings().then(() => {
            props.refreshTable();
            props.setEnableEdit(false);
            props.setEnableDelete(false);
            props.setSelectedConnections({});
        });
    };

    const deleteDenied = () => {
        setShowDeleteConfirmationDialog(false);
    };

    return (
        <div>
            {visible && (
                <LoadOverlay active={pageLoader}>
                    {showDeleteConfirmationDialog && (
                        <div className={"admin-users-delete-dialog-container"}>
                            <Dialog title={"Delete S3 Compatible Setting"} onClose={deleteDenied} className={"admin-users-delete-dialog dialog-standard"}>
                                <p className={"admin-users-delete-dialog-message-text"}>Are you sure you want to delete settings for <strong>{props.s3Settings.connection_name}</strong>?</p>
                                <DialogActionsBar>
                                    <Button className="button-standard button-white" onClick={deleteDenied}>No</Button>
                                    <Button className="button-standard button-submit" onClick={deleteConfirmed}>Yes</Button>
                                </DialogActionsBar>
                            </Dialog>
                        </div>
                    )}

                    <Dialog title={props.createEdit === "Create" ? "New S3 Compatible Connection" : "Edit S3 Compatible Connection"} width={1000} closeIcon={false} className={"dialog-standard ftps3-dialog"}>
                        <div className={"required-fields-dialog-message"}>Fields marked with <span className="required-field">*</span> are required</div>

                        <GridLayout>
                            <GridLayoutItem row={1} col={1}>
                                <div style={{width:"100%"}}>
                                    <Label className="field-label">
                                        Connection Name
                                        <span className="required-field">*</span>
                                    </Label>
                                    <Input
                                        name="connection_name"
                                        minLength={3}
                                        maxLength={64}
                                        value={s3Formik.values.connection_name}
                                        onChange={s3Formik.handleChange}
                                        onBlur={s3Formik.handleBlur}
                                        className={"text-field"}
                                        style={{borderColor: s3Formik.touched.connection_name && s3Formik.errors.connection_name ? "red" : ""}}
                                    />
                                    {s3Formik.touched.connection_name && s3Formik.errors.connection_name && (
                                        <span style={{color: "red"}}>{s3Formik.errors.connection_name}</span>
                                    )}
                                </div>
                            </GridLayoutItem>
                            <GridLayoutItem row={2} col={1}>
                                <div style={{width:"100%", paddingTop:"1.8rem"}}>
                                    <Label className="field-label" style={{width:"100%"}}>
                                        Endpoint Host
                                        <span className="required-field">*</span>
                                    </Label>
                                </div>
                            </GridLayoutItem>
                            <GridLayoutItem row={3} col={1}>
                                <div style={{width:"100%"}}>
                                    <Input
                                        name="host"
                                        maxLength={255}
                                        placeholder="storage.googleapis.com"
                                        value={s3Formik.values.host}
                                        onChange={s3Formik.handleChange}
                                        onBlur={(e) => {
                                            const normalizedHost = normalizeEndpointHost(e.target.value);
                                            s3Formik.setFieldValue("host", normalizedHost);
                                            s3Formik.handleBlur({
                                                ...e,
                                                target: {
                                                    ...e.target,
                                                    name: "host",
                                                    value: normalizedHost
                                                }
                                            });
                                        }}
                                        style={{width: "100%", borderColor: s3Formik.touched.host && s3Formik.errors.host ? "red" : ""}}
                                        className={"text-field"}
                                    />
                                    <div style={{width:"100%"}}>
                                        {s3Formik.touched.host && s3Formik.errors.host && (
                                            <span style={{color: "red"}}>
                                                {s3Formik.errors.host}
                                            </span>
                                        )}
                                    </div>
                                    <div className="k-form-hint">
                                        Enter the provider hostname only, for example "storage.googleapis.com".
                                    </div>
                                </div>
                            </GridLayoutItem>
                            <GridLayoutItem row={4} col={1}>
                                <div style={{width:"100%", paddingTop:"1.8rem"}}>
                                    <Label className="field-label" style={{width:"100%"}}>
                                        Bucket
                                        <span className="required-field">*</span>
                                    </Label>
                                </div>
                            </GridLayoutItem>
                            <GridLayoutItem row={5} col={1}>
                                <div style={{width:"100%"}}>
                                    <Input
                                        name="bucket"
                                        minLength={3}
                                        length={500}
                                        maxLength={1000}
                                        placeholder="my-bucket"
                                        value={s3Formik.values.bucket}
                                        style={{width:"100%"}}
                                        className={"text-field"}
                                        onChange={s3Formik.handleChange}
                                        onBlur={s3Formik.handleBlur}
                                    />
                                    <div className="k-form-hint">
                                        Enter the bucket name only.
                                    </div>
                                    <div>
                                        {s3Formik.touched.bucket && s3Formik.errors.bucket && (
                                            <span style={{color: "red"}}>{s3Formik.errors.bucket}</span>
                                        )}
                                    </div>
                                </div>
                            </GridLayoutItem>
                            <GridLayoutItem row={6} col={1}>
                                <div style={{width:"100%", paddingTop:"1.8rem"}}>
                                    <Label className="field-label" style={{width:"100%"}}>
                                        Prefix / Path
                                    </Label>
                                </div>
                            </GridLayoutItem>
                            <GridLayoutItem row={7} col={1}>
                                <div style={{width:"100%"}}>
                                    <Input
                                        name="prefix"
                                        minLength={0}
                                        length={500}
                                        maxLength={1000}
                                        placeholder="folder/subfolder"
                                        value={s3Formik.values.prefix}
                                        style={{width:"100%"}}
                                        className={"text-field"}
                                        onChange={s3Formik.handleChange}
                                        onBlur={s3Formik.handleBlur}
                                    />
                                    <div className="k-form-hint">
                                        Use this to narrow to a folder inside the bucket. End with "/" when targeting a folder, for example: "folder-1/folder-2/"
                                    </div>
                                </div>
                            </GridLayoutItem>
                            <GridLayoutItem row={8} col={1}>
                                <div style={{width:"100%", paddingTop:"1.8rem"}}>
                                    <Label className="field-label" style={{width:"71%"}}>
                                        Access Key
                                        <span className="required-field">*</span>
                                    </Label>
                                    <Input
                                        name="user_name"
                                        maxLength={255}
                                        value={s3Formik.values.user_name}
                                        onChange={s3Formik.handleChange}
                                        onBlur={s3Formik.handleBlur}
                                        style={{width:"100%", borderColor: s3Formik.touched.user_name && s3Formik.errors.user_name ? "red" : ""}}
                                        className={"text-field"}
                                    />
                                    <div>
                                        {s3Formik.touched.user_name && s3Formik.errors.user_name && (
                                            <span style={{color: "red"}}>{s3Formik.errors.user_name}</span>
                                        )}
                                    </div>
                                </div>
                            </GridLayoutItem>
                            <GridLayoutItem row={9} col={1}>
                                <div style={{width:"100%", paddingTop:"1.8rem"}}>
                                    <Label className="field-label" style={{width:"71%"}}>
                                        Secret Key
                                        <span className="required-field">*</span>
                                    </Label>
                                    <Input
                                        name="password"
                                        type="password"
                                        maxLength={255}
                                        value={s3Formik.values.password}
                                        onChange={s3Formik.handleChange}
                                        onBlur={s3Formik.handleBlur}
                                        style={{width:"100%", borderColor: s3Formik.touched.password && s3Formik.errors.password ? "red" : ""}}
                                        className={"text-field"}
                                    />
                                    <div>
                                        {s3Formik.touched.password && s3Formik.errors.password && (
                                            <span style={{color: "red"}}>{s3Formik.errors.password}</span>
                                        )}
                                    </div>
                                </div>
                            </GridLayoutItem>
                        </GridLayout>
                        <DialogActionsBar>
                            <div className={"dialog-buttons-container"}>
                                <div className={"dialog-buttons-left-div"}>
                                    {props.mode === "EDIT" && (
                                        <Button
                                            fillMode="link"
                                            className={"button-standard button-red dialog-button-delete"}
                                            onClick={async () => {
                                                setShowDeleteConfirmationDialog(true);
                                            }}
                                        >
                                            Delete
                                        </Button>
                                    )}
                                    <Button
                                        className={"button-standard button-white test-con-button"}
                                        disabled={disableTestConnection}
                                        onClick={async () => {
                                            await s3Formik.submitForm();
                                            const errors = await s3Formik.validateForm();
                                            if (!Object.keys(errors).length) {
                                                await testS3CompatibleSettings();
                                            }
                                        }}
                                    >
                                        Test Connection
                                    </Button>
                                </div>
                                <div className={"dialog-buttons-right-div"}>
                                    <Button
                                        className={"button-standard button-white"}
                                        onClick={() => {
                                            props.cancelS3CompatibleSettings();
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        className={"button-standard button-submit"}
                                        disabled={disableSubmit}
                                        onClick={async () => {
                                            temporarilyDisableButton(setDisableSubmit);
                                            await s3Formik.submitForm();
                                            const errors = await s3Formik.validateForm();

                                            if (!Object.keys(errors).length) {
                                                setPageLoader(true);
                                                await saveS3CompatibleSettings();
                                            }
                                        }}
                                    >
                                        Save
                                    </Button>
                                </div>
                            </div>
                        </DialogActionsBar>
                    </Dialog>
                </LoadOverlay>
            )}
        </div>
    );
};

export default S3CompatibleSettings;
